import { useMemo, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { useDetailNavigation } from "@/contexts/DetailNavigationContext";
import { parseHubModalUrlState } from "@/lib/modals/url/modal-url-config";
import type { EntityHooks, BaseEntity } from "../../UniversalCRMTable/types/shared";

export function useDetailModalData<T extends BaseEntity>({
  modalId,
  entityName,
  hooks,
  itemIds,
  currentItemIndex,
}: {
  modalId: string;
  entityName: string;
  hooks: Pick<EntityHooks<T>, "useSingle">;
  itemIds?: string[];
  currentItemIndex?: number;
}) {
  const searchParams = useSearchParams();

  // Compute item ID: from URL search params or from props (e.g. openModal with data from address map)
  const itemId = useMemo(() => {
    // First priority: central modal URL parser for the currently active modal
    const parsed = parseHubModalUrlState(searchParams);
    if (parsed && parsed.modalId === modalId && parsed.entityId) {
      return parsed.entityId;
    }

    // Second priority: From props when modal opened with data (e.g. address map "View details")
    if (itemIds?.length && currentItemIndex !== undefined && itemIds[currentItemIndex]) {
      return itemIds[currentItemIndex];
    }

    return "";
  }, [searchParams, modalId, itemIds, currentItemIndex]);

  // Fetch item data via universal hooks contract
  const itemQuery = hooks.useSingle(itemId, { enabled: !!itemId });

  const item = itemQuery.data;
  const isLoading = itemQuery.isLoading;
  const error = itemQuery.error;
  const refetch = itemQuery.refetch;

  // Initialize navigation context when modal opens
  const {
    setContext,
    ids: currentIds,
    index: currentIndex,
    entityName: currentEntityName,
  } = useDetailNavigation();

  useEffect(() => {
    if (!itemId) return;

    // Helper to compare arrays by content
    const arraysEqual = (a: string[], b: string[]): boolean => {
      if (a.length !== b.length) return false;
      return a.every((val, idx) => val === b[idx]);
    };

    // Determine what context values we want to set
    const targetIds = itemIds && currentItemIndex !== undefined ? itemIds : [itemId];
    const targetIndex = itemIds && currentItemIndex !== undefined ? currentItemIndex : 0;

    // Only update if values actually changed (prevent infinite loop)
    const idsChanged = !arraysEqual(currentIds, targetIds);
    const indexChanged = currentIndex !== targetIndex;
    const entityNameChanged = currentEntityName !== entityName;

    if (idsChanged || indexChanged || entityNameChanged) {
      setContext(targetIds, targetIndex, entityName);
    }
  }, [itemId, itemIds, currentItemIndex, entityName, setContext, currentIds, currentIndex, currentEntityName]);

  return {
    itemId,
    item,
    isLoading,
    error,
    refetch,
    navigationContext: {
      ids: currentIds,
      index: currentIndex,
      entityName: currentEntityName,
      setContext,
    },
  };
}
